import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, Clock, Users, BadgePoundSterling } from "lucide-react";
import { services } from "../data/services";
import { profileFor } from "../data/serviceProfiles";
import { subBrandFor } from "../data/subBrands";
import CTABanner from "../components/CTABanner";
import FaqAccordion from "../components/FaqAccordion";
import StickyServiceCta from "../components/StickyServiceCta";
import FloatingAssessmentCTA from "../components/FloatingAssessmentCTA";
import StarMotif from "../components/StarMotif";
import { ServiceLineIcon } from "../components/DeliveryIcons";

const byslug = (slug) => services.find((s) => s.slug === slug);

const ServicePage = () => {
  const { slug } = useParams();
  const service = byslug(slug);
  const profile = service ? profileFor(slug) : null;
  const subBrand = service ? subBrandFor(slug) : null;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [slug]);

  // FAQ JSON-LD for SEO rich results
  useEffect(() => {
    if (!profile?.faqs?.length) return;
    let s = document.head.querySelector('script[data-jsonld="faq"]');
    if (!s) {
      s = document.createElement("script");
      s.type = "application/ld+json";
      s.setAttribute("data-jsonld", "faq");
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: profile.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });
    return () => {
      const cur = document.head.querySelector('script[data-jsonld="faq"]');
      if (cur) cur.remove();
    };
  }, [profile, slug]);

  if (!service) return <Navigate to="/services" replace />;

  const related = (service.related || []).map(byslug).filter(Boolean);

  return (
    <>
      {/* HERO */}
      <section className="ek-gradient-dark text-white relative overflow-hidden">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <Link to="/services" className="ek-eyebrow text-[#A179FA] inline-flex items-center gap-2 hover:text-white transition-colors">
            ← All services
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-6">
            <div className="lg:col-span-9">
              <div className="flex items-center gap-3 flex-wrap mb-4" data-testid="service-subbrand">
                {subBrand ? (
                  <>
                    <ServiceLineIcon code={subBrand.code} className="w-7 h-7 text-[#C9B8FB] shrink-0" />
                    <span className="inline-flex items-center px-3 py-1 text-[10.5px] font-bold tracking-[0.18em] text-white bg-[#7737F6] uppercase">
                      {subBrand.code}
                    </span>
                  </>
                ) : null}
                <span className="text-[11px] font-semibold tracking-[0.22em] text-zinc-400 uppercase">
                  EKSELENS360 · {service.name}
                </span>
              </div>
              {subBrand?.tagline ? (
                <p className="text-[#A179FA] text-sm md:text-base font-light italic mb-4" data-testid="service-subbrand-tagline">
                  {subBrand.tagline}
                </p>
              ) : null}
              <h1
                className="ek-heading text-4xl md:text-5xl lg:text-6xl text-white leading-tight"
                data-testid="service-page-headline"
              >
                {service.hero}
              </h1>
              <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-8 max-w-3xl">
                {service.intro}
              </p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" className="ek-btn ek-btn-primary" data-testid="service-hero-cta">
                  Book a discovery conversation <ArrowRight size={16} />
                </Link>
                <Link to="/services" className="ek-btn ek-btn-outline-light">
                  Explore other services
                </Link>
              </div>

              {profile?.pills ? (
                <div className="mt-8 flex flex-wrap gap-x-8 gap-y-3 text-[13px] text-zinc-300" data-testid="service-pills">
                  <span className="inline-flex items-center gap-2">
                    <Clock size={14} className="text-[#A179FA]" /> {profile.pills.length}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <Users size={14} className="text-[#A179FA]" /> {profile.pills.team}
                  </span>
                  <span className="inline-flex items-center gap-2">
                    <BadgePoundSterling size={14} className="text-[#A179FA]" /> {profile.pills.rateBand}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </section>

      {/* CHALLENGES */}
      {service.challenges?.length > 0 && (
        <section className="bg-white ek-section">
          <div className="ek-container">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-5">
                <div className="ek-eyebrow">Common client challenges</div>
                <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black">
                  The signals we hear most often.
                </h2>
              </div>
              <ul className="lg:col-span-7 space-y-4">
                {service.challenges.map((c, i) => (
                  <li key={i} className="flex items-start gap-4 border-b border-zinc-200 pb-4" data-testid={`challenge-${i}`}>
                    <div className="text-[11px] font-semibold tracking-[0.18em] text-[#7737F6] uppercase mt-1 shrink-0 w-8">
                      0{i + 1}
                    </div>
                    <p className="ek-body text-[16px] flex-1">{c}</p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* HOW WE HELP */}
      <section className="bg-[#F2F2F3] ek-section">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <div className="ek-eyebrow">How Ekselens helps</div>
              <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black">
                A structured way through.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="ek-body text-lg md:text-xl">{service.howWeHelp}</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHAT WE DELIVER */}
      <section className="bg-white ek-section">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
            <div className="lg:col-span-5">
              <div className="ek-eyebrow">What we deliver</div>
              <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black">
                Practical, client-ready outputs.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="ek-body text-lg">
                Every engagement produces outputs your team can use the day after we finish.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-200">
            {service.delivers.map((d, i) => (
              <div key={i} className="bg-white p-6 lg:p-8 flex items-start gap-4" data-testid={`delivers-${i}`}>
                <div className="w-7 h-7 shrink-0 bg-[#F2F2F3] text-[#7737F6] flex items-center justify-center">
                  <Check size={14} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="text-[11px] font-semibold tracking-[0.18em] text-zinc-500 uppercase mb-1">
                    Output {String(i + 1).padStart(2, "0")}
                  </div>
                  <p className="ek-heading text-base text-black">{d}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OUTPUTS / WHY THIS MATTERS */}
      <section className="ek-gradient-dark-soft text-white ek-section relative overflow-hidden">
        <div className="ek-star-watermark-left" />
        <div className="ek-container relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="ek-eyebrow text-[#A179FA]">Typical engagement outputs</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-white">
              What you leave with.
            </h2>
            <ul className="mt-8 space-y-3">
              {service.outputs.map((o, i) => (
                <li key={i} className="flex items-start gap-3 text-zinc-200 font-light text-[16px]">
                  <span className="text-[#A179FA] mt-1.5">●</span>
                  {o}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="ek-eyebrow text-[#A179FA]">Why this matters</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-white">
              The board-level outcome.
            </h2>
            <p className="text-zinc-200 font-light text-lg leading-relaxed mt-8">
              {service.whyItMatters}
            </p>
          </div>
        </div>
      </section>

      {/* RELATED SERVICES */}
      {related.length > 0 && (
        <section className="bg-[#F2F2F3] ek-section">
          <div className="ek-container">
            <div className="ek-eyebrow">Related services</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black mb-12">
              Often considered together.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  className="ek-card block group"
                  data-testid={`related-service-${r.slug}`}
                >
                  <StarMotif size={18} color="#7737F6" />
                  <h3 className="ek-heading text-lg text-black mt-4 leading-snug">{r.name}</h3>
                  <p className="ek-body text-[14px] mt-3">{r.short}</p>
                  <div className="mt-5 inline-flex items-center gap-2 text-[#7737F6] text-sm font-medium">
                    Learn more <ArrowRight size={14} />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {profile?.faqs?.length ? (
        <FaqAccordion items={profile.faqs} title={`${service.name} — common questions.`} />
      ) : null}

      <CTABanner
        eyebrow={service.name}
        title="Discuss this engagement."
        body="A focused conversation will help us understand your context, identify the right starting point and define practical next steps."
      />

      <StickyServiceCta serviceName={service.name} />
      <FloatingAssessmentCTA serviceSlug={service.slug} />
    </>
  );
};

export default ServicePage;
